'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { PlayIcon, PauseIcon, ArrowCounterClockwiseIcon, ArrowClockwiseIcon } from '@phosphor-icons/react'

interface BlogListenButtonProps {
  targetSelector?: string
  text?: string
  /** Pre-generated audio URL (MP3/WAV). When provided, uses HTML Audio instead of Web Speech API. */
  audioSrc?: string
  /** Optional sidecar JSON with word-level timestamps from STT. Enables accurate highlight-while-listening. */
  audioTimings?: string
}

interface WordTiming { word: string; start: number; end: number }
interface AlignedTiming { start: number; end: number; charStart: number; charEnd: number }

// ::highlight() rules must be injected via JS — Turbopack rejects them in CSS files.
// Colors match seasons-component-library: cyber-blue #053bee at 8% (sentence) / 16% (word).
const HIGHLIGHT_STYLE_ID = 'blog-listen-highlight-style'
const HIGHLIGHT_DISABLED = false

function injectHighlightStyle() {
  if (typeof document === 'undefined' || document.getElementById(HIGHLIGHT_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = HIGHLIGHT_STYLE_ID
  style.textContent = '::highlight(listen-active) { background-color: rgba(5,59,238,0.16); color: #000a1b; }'
  document.head.appendChild(style)
}

const RATE_OPTIONS = [0.75, 1, 1.25, 1.5, 1.75, 2] as const
const SKIP_SECONDS = 15
const WORDS_PER_MINUTE = 160
const BLOCK_TAGS = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'LI', 'TD', 'TH', 'DT', 'DD', 'FIGCAPTION', 'CITE',
])

interface TextNodeEntry { node: Text; start: number; end: number }

function formatTime(s: number) {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}

function buildTextMap(root: HTMLElement) {
  const textNodes: TextNodeEntry[] = []
  let cum = 0
  let full = ''
  let currentBlock: HTMLElement | null = null

  const SKIP_TAGS = new Set(['CODE', 'STYLE', 'SCRIPT', 'NOSCRIPT', 'SVG', 'CANVAS'])
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      let cur: Node | null = node.parentNode
      while (cur && cur !== root) {
        if (cur.nodeType === 1) {
          const el = cur as HTMLElement
          if (el.getAttribute('data-no-listen') === 'true') return NodeFilter.FILTER_REJECT
          if (SKIP_TAGS.has(el.tagName)) return NodeFilter.FILTER_REJECT
        }
        cur = cur.parentNode
      }
      return NodeFilter.FILTER_ACCEPT
    },
  })
  let node: Node | null
  while ((node = walker.nextNode())) {
    const t = node as Text
    const text = t.data
    if (!text || !text.trim()) continue

    let cur: Node | null = t.parentNode
    let blockAncestor: HTMLElement | null = null
    while (cur && cur !== root) {
      if (cur.nodeType === 1 && BLOCK_TAGS.has((cur as HTMLElement).tagName)) {
        blockAncestor = cur as HTMLElement
        break
      }
      cur = cur.parentNode
    }

    if (currentBlock && blockAncestor && blockAncestor !== currentBlock) {
      full += ' '
      cum += 1
    }
    currentBlock = blockAncestor

    const start = cum
    const end = cum + text.length
    textNodes.push({ node: t, start, end })
    cum = end
    full += text
  }
  return { fullText: full, textNodes }
}

function buildRangeForChars(textNodes: TextNodeEntry[], start: number, end: number): Range | null {
  if (typeof document === 'undefined' || !textNodes.length) return null
  const range = document.createRange()
  let startSet = false
  let lastTouched: TextNodeEntry | null = null
  for (const tn of textNodes) {
    if (tn.end <= start) continue
    if (tn.start >= end) break
    if (!startSet) {
      const off = Math.max(0, start - tn.start)
      range.setStart(tn.node, Math.min(off, tn.node.data.length))
      startSet = true
    }
    lastTouched = tn
    if (end <= tn.end) {
      range.setEnd(tn.node, Math.max(0, end - tn.start))
      return range
    }
  }
  if (startSet && lastTouched) {
    range.setEnd(lastTouched.node, lastTouched.node.data.length)
    return range
  }
  return null
}

// ─── Word-timing alignment helpers ─────────────────────────────────────────────
//
// STT-generated word timings are aligned to the DOM text via a rolling pointer.
// For each STT word, we look up to N positions ahead in the DOM word stream to
// find a match (normalized: lowercased, alphanumerics only). Matched timings
// gain a (charStart, charEnd) pointing into the DOM `fullText`; unmatched
// timings (rare STT errors) are dropped — the rolling pointer still advances
// correctly because we only consume DOM words when matched.
//
// Special case: TTS engines expand numbers ("1997" → "nineteen ninety seven",
// "$30M" → "thirty million dollars"). When an STT word is a number-related word
// and the current DOM word is numeric, we map the highlight to that DOM number
// WITHOUT advancing the pointer, so the numeric DOM token stays highlighted for
// the entire spoken expansion before the pointer moves to the next real word.

function normalizeWord(w: string): string {
  return w.toLowerCase().replace(/[^a-z0-9]/g, '')
}

// Number-related words that TTS engines produce when expanding numeric tokens.
// When one of these appears in the STT stream and the current DOM word is purely
// numeric (starts with a digit after normalization), we highlight the DOM number
// without advancing the rolling domIdx pointer.
const NUMBER_WORDS = new Set([
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen', 'twenty', 'thirty', 'forty', 'fifty',
  'sixty', 'seventy', 'eighty', 'ninety', 'hundred', 'thousand', 'million',
  'billion', 'trillion', 'percent', 'dollars', 'cents',
])

function buildDomWordPositions(fullText: string): { word: string; charStart: number }[] {
  const positions: { word: string; charStart: number }[] = []
  const wordRe = /\S+/g
  let m: RegExpExecArray | null
  while ((m = wordRe.exec(fullText))) {
    positions.push({ word: m[0], charStart: m.index })
  }
  return positions
}

function alignTimingsToDom(
  timings: WordTiming[],
  domWords: { word: string; charStart: number }[],
): AlignedTiming[] {
  const aligned: AlignedTiming[] = []
  const LOOK_AHEAD = 8
  // Prefix matching only kicks in when both sides are long enough to be unambiguous.
  // Without this floor, short words like "in", "to", "he" falsely match long DOM
  // words ("including", "total", "health"), jumping the pointer forward.
  const MIN_PREFIX = 4

  // wordMatch — exact or prefix match guarded by MIN_PREFIX
  const wordMatch = (a: string, b: string) =>
    a === b ||
    (a.length >= MIN_PREFIX && b.startsWith(a)) ||
    (b.length >= MIN_PREFIX && a.startsWith(b))

  // ── Multi-window anchor search ────────────────────────────────────────────────
  //
  // TTS audio always starts with the post title (prepended in audio-generation.ts),
  // but `article.blog-prose` does NOT include the title element. So the first N
  // non-trivial STT words are title words that will never appear in the DOM, causing
  // the single-window anchor to fail and the pointer to start at 0.
  //
  // Fix: try several windows of 5 non-trivial STT words at offsets 0, 8, 16, 24 …
  // The window that scores highest against the first 400 DOM words wins.
  // This lets the anchor skip the title and lock onto the article body.
  //
  // Once the best window's DOM position is found, we back-calculate domIdx so that
  // the STT words that come BEFORE the anchor window (title narration) are mapped
  // to pre-anchor DOM positions — they won't match anything there and will be
  // silently dropped, which is correct (title isn't in the article DOM).

  // Collect raw STT indices of non-trivial words (length > 3 after normalize)
  const nonTrivIdx: number[] = []
  for (let i = 0; i < timings.length; i++) {
    if (normalizeWord(timings[i].word).length > 3) nonTrivIdx.push(i)
  }

  let domIdx = 0

  if (nonTrivIdx.length >= 2) {
    const ANCHOR_N = 5
    const searchLimit = Math.min(domWords.length - ANCHOR_N, 400)
    // Try anchor windows every 8 non-trivial words; stop once we get a perfect score
    const windowStarts = [0, 8, 16, 24, 32].filter(s => s < nonTrivIdx.length)

    let bestScore = 0
    let bestDomPos = 0
    let bestSttAnchorRawIdx = 0  // raw (absolute) STT index of first anchor word in winning window

    for (const ws of windowStarts) {
      const anchorWords = nonTrivIdx
        .slice(ws, ws + ANCHOR_N)
        .map(i => normalizeWord(timings[i].word))
      if (anchorWords.length < 2) continue

      let wBestPos = 0
      let wBestScore = 0
      for (let di = 0; di < searchLimit; di++) {
        let score = 0
        for (let k = 0; k < anchorWords.length; k++) {
          const dn = normalizeWord(domWords[di + k]?.word ?? '')
          if (wordMatch(dn, anchorWords[k])) score++
        }
        if (score > wBestScore) { wBestScore = score; wBestPos = di }
        if (wBestScore >= anchorWords.length) break
      }

      if (wBestScore > bestScore) {
        bestScore = wBestScore
        bestDomPos = wBestPos
        bestSttAnchorRawIdx = nonTrivIdx[ws]
      }
      if (bestScore >= ANCHOR_N) break
    }

    if (bestScore >= 2) {
      // bestDomPos = DOM word index of the first anchor word in the winning window
      // bestSttAnchorRawIdx = how many STT words come before that anchor
      // We back off that many positions in the DOM so leading STT words map to
      // DOM positions before the anchor (they'll fail to match and be dropped).
      domIdx = Math.max(0, bestDomPos - bestSttAnchorRawIdx)
    }
  }

  // ── Main alignment loop ───────────────────────────────────────────────────────
  for (const sttWord of timings) {
    const sttNorm = normalizeWord(sttWord.word)
    if (!sttNorm) continue

    // Number-expansion fast path: TTS expands numbers ("$30M" → "thirty million
    // dollars"). When the STT word is a number-related word and the current DOM
    // word is numeric, highlight the DOM number without advancing the pointer.
    if (NUMBER_WORDS.has(sttNorm)) {
      const currentDom = domWords[domIdx]
      if (currentDom) {
        const currentNorm = normalizeWord(currentDom.word)
        if (currentNorm && /^\d/.test(currentNorm)) {
          aligned.push({
            start: sttWord.start,
            end: sttWord.end,
            charStart: currentDom.charStart,
            charEnd: currentDom.charStart + currentDom.word.length,
          })
          continue
        }
      }
      // Number expansion word but no numeric DOM token — skip entirely.
      continue
    }

    for (let i = 0; i < LOOK_AHEAD; i++) {
      const candidate = domWords[domIdx + i]
      if (!candidate) break
      const domNorm = normalizeWord(candidate.word)
      if (!domNorm) continue
      if (wordMatch(sttNorm, domNorm)) {
        aligned.push({
          start: sttWord.start,
          end: sttWord.end,
          charStart: candidate.charStart,
          charEnd: candidate.charStart + candidate.word.length,
        })
        domIdx += i + 1
        break
      }
    }
  }
  return aligned
}

function findAlignedAtTime(aligned: AlignedTiming[], t: number): AlignedTiming | null {
  if (!aligned.length) return null
  let lo = 0
  let hi = aligned.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (aligned[mid].start <= t) {
      found = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return found >= 0 ? aligned[found] : null
}

// Route GCS fetch through the Next.js /gcs proxy to avoid CORS restrictions.
// Audio <audio> elements play GCS MP3s directly (browser bypasses CORS for media),
// but fetch() for the .timings.json sidecar is a full CORS request.
const GCS_PREFIX = 'https://storage.googleapis.com/cms_rewire/'
function toProxyUrl(url: string): string {
  return url.startsWith(GCS_PREFIX) ? '/gcs/' + url.slice(GCS_PREFIX.length) : url
}

// ─── Audio-src mode ────────────────────────────────────────────────────────────

function AudioSrcPlayer({
  audioSrc,
  audioTimings,
  targetSelector = 'article.blog-prose',
}: {
  audioSrc: string
  audioTimings?: string
  targetSelector?: string
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [duration, setDuration] = useState(0)
  const [rate, setRate] = useState<number>(1)
  const [loaded, setLoaded] = useState(false)
  const [audioFailed, setAudioFailed] = useState(false)

  const fullTextRef = useRef('')
  const textNodesRef = useRef<TextNodeEntry[]>([])
  const alignedTimingsRef = useRef<AlignedTiming[]>([])
  const timingsFetchedRef = useRef(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wordHighlightRef = useRef<any>(null)
  const lastWordStartRef = useRef<number>(-1)
  const rafIdRef = useRef<number>(0)

  const updateWordHighlight = useCallback((charStart: number, charEnd: number) => {
    if (HIGHLIGHT_DISABLED) return
    const hi = wordHighlightRef.current
    if (!hi || !textNodesRef.current.length) return
    if (charStart === lastWordStartRef.current) return
    lastWordStartRef.current = charStart
    const range = buildRangeForChars(textNodesRef.current, charStart, charEnd)
    if (!range) return
    hi.clear()
    hi.add(range)
  }, [])

  useEffect(() => {
    injectHighlightStyle()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const W = window as any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const C = CSS as any
    if ('Highlight' in window && C?.highlights?.set) {
      wordHighlightRef.current = new W.Highlight()
      C.highlights.set('listen-active', wordHighlightRef.current)
    }

    const audio = new Audio()
    audio.preload = 'metadata'
    audio.volume = 1
    audio.muted = false
    audio.src = audioSrc
    audio.load()
    audioRef.current = audio

    audio.onloadedmetadata = () => {
      setDuration(audio.duration)
      setLoaded(true)
      const root = document.querySelector(targetSelector) as HTMLElement | null
      if (root) {
        const { fullText, textNodes } = buildTextMap(root)
        fullTextRef.current = fullText
        textNodesRef.current = textNodes

        // Try to load + align word-level timings.
        if (audioTimings) {
          // Mark as fetched only when the DOM text is present so the onplay
          // handler can retry if onloadedmetadata fired before article rendered.
          const hasDomText = fullText.length > 0
          if (hasDomText) timingsFetchedRef.current = true
          fetch(toProxyUrl(audioTimings))
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => {
              const words: WordTiming[] = Array.isArray(json?.words) ? json.words : []
              if (!words.length) return
              const domWords = buildDomWordPositions(fullText)
              const aligned = alignTimingsToDom(words, domWords)
              // Sanity check: at least 10% of STT words should align with the DOM.
              // Keeps obviously-wrong timings out while accepting posts whose DOM
              // has mixed content (links, code, embedded elements).
              if (aligned.length >= Math.max(5, Math.floor(words.length * 0.10))) {
                alignedTimingsRef.current = aligned
              }
            })
            .catch(() => { /* non-fatal */ })
        }
      }
    }
    // RAF loop: runs at 60fps while audio is playing for smooth highlight updates.
    let hlRaf = 0
    const runHlLoop = () => {
      if (audio.paused || audio.ended) return
      const ct = audio.currentTime
      const aligned = alignedTimingsRef.current
      if (aligned.length > 0) {
        const hit = findAlignedAtTime(aligned, ct)
        if (hit) updateWordHighlight(hit.charStart, hit.charEnd)
      }
      hlRaf = requestAnimationFrame(runHlLoop)
    }

    // ontimeupdate now only drives the scrubber/time display (throttled via RAF).
    audio.ontimeupdate = () => {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = requestAnimationFrame(() => setElapsed(audio.currentTime))
    }
    audio.onplay = () => {
      setPlaying(true)
      // Always rebuild text map on first play (or if empty) to handle the race
      // where onloadedmetadata fires before React has fully rendered all blocks.
      const needsTextMap = !fullTextRef.current.length
      if (needsTextMap) {
        const root = document.querySelector(targetSelector) as HTMLElement | null
        if (root) {
          const { fullText, textNodes } = buildTextMap(root)
          fullTextRef.current = fullText
          textNodesRef.current = textNodes
        }
      }
      // Re-fetch and align timings if onloadedmetadata fired before the article
      // was in the DOM (domWords was empty then, so alignment returned 0).
      if (!timingsFetchedRef.current && audioTimings && fullTextRef.current.length > 0) {
        timingsFetchedRef.current = true
        const fullText = fullTextRef.current
        fetch(toProxyUrl(audioTimings))
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => {
            const words: WordTiming[] = Array.isArray(json?.words) ? json.words : []
            if (!words.length) return
            const domWords = buildDomWordPositions(fullText)
            const aligned = alignTimingsToDom(words, domWords)
            if (aligned.length >= Math.max(5, Math.floor(words.length * 0.10))) {
              alignedTimingsRef.current = aligned
            }
          })
          .catch(() => { /* non-fatal */ })
      }
      cancelAnimationFrame(hlRaf)
      hlRaf = requestAnimationFrame(runHlLoop)
    }
    audio.onpause = () => {
      setPlaying(false)
      cancelAnimationFrame(hlRaf)
    }
    audio.onended = () => {
      setPlaying(false)
      cancelAnimationFrame(hlRaf)
      setElapsed(audio.duration)
      if (wordHighlightRef.current) wordHighlightRef.current.clear()
      lastWordStartRef.current = -1
    }
    audio.onerror = () => {
      // Audio URL is inaccessible — fall back to Web Speech API player
      setAudioFailed(true)
    }

    return () => {
      // Null handlers first so that setting src='' doesn't fire onerror/onplay
      // on a stale element and corrupt state for the next render (React Strict Mode
      // fires effects twice in development).
      cancelAnimationFrame(hlRaf)
      cancelAnimationFrame(rafIdRef.current)
      audio.onloadedmetadata = null
      audio.ontimeupdate = null
      audio.onplay = null
      audio.onpause = null
      audio.onended = null
      audio.onerror = null
      audio.pause()
      audio.src = ''
      if (wordHighlightRef.current) wordHighlightRef.current.clear()
      lastWordStartRef.current = -1
      alignedTimingsRef.current = []
      timingsFetchedRef.current = false
    }
  }, [audioSrc, audioTimings, targetSelector, updateWordHighlight])

  const handlePlayPause = () => {
    const audio = audioRef.current
    if (!audio) return
    if (!expanded) setExpanded(true)
    if (audio.paused) {
      // Rebuild text map lazily on first play if onloadedmetadata ran before
      // the article content was fully in the DOM (e.g. very fast cache hit).
      if (!fullTextRef.current.length) {
        const root = document.querySelector(targetSelector) as HTMLElement | null
        if (root) {
          const { fullText, textNodes } = buildTextMap(root)
          fullTextRef.current = fullText
          textNodesRef.current = textNodes
        }
      }
      // Play even if metadata hasn't fully loaded — browser will buffer
      audio.play().catch(() => setAudioFailed(true))
    } else {
      audio.pause()
    }
  }

  const skipBy = (deltaSec: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + deltaSec))
  }

  const cycleRate = () => {
    const audio = audioRef.current
    if (!audio) return
    const idx = RATE_OPTIONS.indexOf(rate as typeof RATE_OPTIONS[number])
    const next = RATE_OPTIONS[(idx + 1) % RATE_OPTIONS.length]
    setRate(next)
    audio.playbackRate = next
  }

  const seekTo = (ratio: number) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    audio.currentTime = Math.max(0, Math.min(duration, ratio * duration))
  }

  const progress = duration > 0 ? Math.min(1, elapsed / duration) : 0

  // Audio URL failed to load — fall back to Web Speech synthesis
  if (audioFailed) {
    return <WebSpeechPlayer targetSelector={targetSelector} />
  }

  if (!expanded) {
    return (
      <button
        type="button"
        className="blog-listen-pill"
        onClick={handlePlayPause}
        aria-label="Listen to article"
      >
        <span className="blog-listen-pill__icon" aria-hidden="true">
          {!loaded
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></svg>
            : <PlayIcon size={14} weight="fill" />}
        </span>
        <span className="blog-listen-pill__label">Listen to article</span>
        <span className="blog-listen-pill__sep" aria-hidden="true" />
        <span className="blog-listen-pill__time">{loaded ? formatTime(duration) : '...'}</span>
      </button>
    )
  }

  return (
    <div className="blog-listen-player blog-listen-player--expanded" role="region" aria-label="Article audio player">
      <button
        type="button"
        className="blog-listen-player__play"
        onClick={handlePlayPause}
        aria-label={playing ? 'Pause article' : 'Play article'}
      >
        {playing ? <PauseIcon size={18} weight="fill" /> : <PlayIcon size={18} weight="fill" />}
      </button>

      <span className="blog-listen-player__time">
        {formatTime(elapsed)}<span className="blog-listen-player__time-sep"> / </span>{formatTime(duration)}
      </span>

      <button
        type="button"
        className="blog-listen-player__skip"
        onClick={() => skipBy(-SKIP_SECONDS)}
        aria-label={`Skip back ${SKIP_SECONDS} seconds`}
      >
        <ArrowCounterClockwiseIcon size={14} weight="bold" />
        <span className="blog-listen-player__skip-num">{SKIP_SECONDS}</span>
      </button>

      <div
        className="blog-listen-player__scrubber"
        role="slider"
        tabIndex={0}
        aria-label="Audio position"
        aria-valuemin={0}
        aria-valuemax={duration || 1}
        aria-valuenow={Math.round(elapsed)}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          seekTo((e.clientX - rect.left) / rect.width)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); skipBy(-5) }
          if (e.key === 'ArrowRight') { e.preventDefault(); skipBy(5) }
        }}
      >
        <div className="blog-listen-player__scrubber-track" />
        <div className="blog-listen-player__scrubber-fill" style={{ width: `${progress * 100}%` }} />
        <div className="blog-listen-player__scrubber-thumb" style={{ left: `${progress * 100}%` }} />
      </div>

      <button
        type="button"
        className="blog-listen-player__skip"
        onClick={() => skipBy(SKIP_SECONDS)}
        aria-label={`Skip ahead ${SKIP_SECONDS} seconds`}
      >
        <span className="blog-listen-player__skip-num">{SKIP_SECONDS}</span>
        <ArrowClockwiseIcon size={14} weight="bold" />
      </button>

      <button
        type="button"
        className="blog-listen-player__rate"
        onClick={cycleRate}
        aria-label={`Playback speed ${rate} times. Click to change.`}
      >
        {rate}x
      </button>
    </div>
  )
}

// ─── Web Speech mode (existing implementation) ─────────────────────────────────

function WebSpeechPlayer({
  targetSelector = 'article.blog-prose',
  text,
}: { targetSelector?: string; text?: string }) {
  const [supported, setSupported] = useState(true)
  const [playing, setPlaying] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [duration, setDuration] = useState(0)
  const [rate, setRate] = useState<number>(1)

  const fullTextRef = useRef('')
  const textNodesRef = useRef<TextNodeEntry[]>([])
  const charOffsetRef = useRef(0)
  const charPerSecRef = useRef(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wordHighlightRef = useRef<any>(null)
  const lastWordStartRef = useRef<number>(-1)
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null)
  const speakStartRef = useRef<number>(0)

  const recomputeDuration = useCallback((rateValue: number) => {
    const t = fullTextRef.current
    const words = t.trim().split(/\s+/).filter(Boolean).length
    if (!words) { setDuration(0); return 0 }
    const seconds = Math.round((words / (WORDS_PER_MINUTE * rateValue)) * 60)
    setDuration(seconds)
    charPerSecRef.current = t.length / Math.max(seconds, 1)
    return seconds
  }, [])

  const ensureMap = useCallback(() => {
    if (text) {
      fullTextRef.current = text
      textNodesRef.current = []
      return
    }
    const root = document.querySelector(targetSelector) as HTMLElement | null
    if (!root) return
    const { fullText, textNodes } = buildTextMap(root)
    fullTextRef.current = fullText
    textNodesRef.current = textNodes
  }, [targetSelector, text])

  useEffect(() => {
    injectHighlightStyle()
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setSupported(false)
      return
    }

    const pickBestVoice = () => {
      const voices = window.speechSynthesis.getVoices()
      if (!voices.length) return
      voiceRef.current =
        voices.find(v => v.name === 'Google US English') ||
        voices.find(v => v.name.includes('Google') && v.lang.startsWith('en')) ||
        voices.find(v => v.name.includes('Samantha')) ||
        voices.find(v => v.name.includes('Microsoft') && v.lang === 'en-US') ||
        voices.find(v => v.lang === 'en-US') ||
        voices.find(v => v.lang.startsWith('en')) ||
        null
    }
    pickBestVoice()
    // Save the previous handler so cleanup can restore it instead of nulling
    // a handler installed by some other component (avoids a global handler leak
    // when multiple narrators mount/unmount across navigations).
    const prevHandler = window.speechSynthesis.onvoiceschanged;
    window.speechSynthesis.onvoiceschanged = pickBestVoice;

    const id = setTimeout(() => {
      ensureMap()
      recomputeDuration(rate)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const W = window as any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const C = CSS as any
      if ('Highlight' in window && C && C.highlights && typeof C.highlights.set === 'function') {
        wordHighlightRef.current = new W.Highlight()
        C.highlights.set('listen-active', wordHighlightRef.current)
      }
    }, 120)
    return () => {
      clearTimeout(id)
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel()
        // Restore the prior handler (usually null) instead of leaving our
        // closure attached after unmount.
        window.speechSynthesis.onvoiceschanged = prevHandler
      }
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (keepAliveRef.current) clearInterval(keepAliveRef.current)
      if (wordHighlightRef.current) wordHighlightRef.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopTicker = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
  }

  const startTicker = () => {
    stopTicker()
    let elapsedTick = 0
    intervalRef.current = setInterval(() => {
      // Update highlight at 20fps (every 50ms).
      if (speakStartRef.current > 0) {
        const secs = (Date.now() - speakStartRef.current) / 1000
        const charPos = charOffsetRef.current + Math.round(secs * charPerSecRef.current)
        updateHighlight(charPos)
      }
      // Update elapsed state at ~4fps (every 5th tick = 250ms wall time) to
      // avoid flooding React with re-renders on the scrubber/time display.
      elapsedTick++
      if (elapsedTick >= 5) {
        elapsedTick = 0
        setElapsed((e) => {
          const next = e + 0.25
          if (duration && next >= duration) { stopTicker(); return duration }
          return next
        })
      }
    }, 50)
  }

  const stopKeepAlive = () => {
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null }
  }

  const startKeepAlive = () => {
    stopKeepAlive()
    // Chrome pauses speechSynthesis after ~15s; resume() keeps it alive.
    keepAliveRef.current = setInterval(() => {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume()
    }, 10000)
  }

  const updateHighlight = (absChar: number, wordLength?: number) => {
    if (HIGHLIGHT_DISABLED) return
    if (!textNodesRef.current.length || !fullTextRef.current.length) return
    const wHi = wordHighlightRef.current
    if (wHi && wordLength && wordLength > 0 && absChar !== lastWordStartRef.current) {
      lastWordStartRef.current = absChar
      const wordRange = buildRangeForChars(textNodesRef.current, absChar, absChar + wordLength)
      if (wordRange) { wHi.clear(); wHi.add(wordRange) }
    }
  }

  const speakFromOffset = (charOffset: number, rateValue: number) => {
    const synth = window.speechSynthesis
    synth.cancel()
    ensureMap()
    const fullText = fullTextRef.current
    const text = fullText.slice(Math.max(0, charOffset))
    if (!text.trim()) { setPlaying(false); stopTicker(); stopKeepAlive(); return }
    const utt = new SpeechSynthesisUtterance(text)
    utt.rate = rateValue
    utt.pitch = 1
    utt.lang = 'en-US'
    if (voiceRef.current) utt.voice = voiceRef.current
    utt.onstart = () => {
      speakStartRef.current = Date.now()
      setPlaying(true)
      startTicker()
      startKeepAlive()
    }
    utt.onboundary = (e) => {
      // SpeechSynthesisEvent exposes charLength on most browsers (Chrome ≥58,
      // Edge, Safari 14+). Fall back to estimating from the next whitespace
      // when unavailable so we still get word-level highlighting.
      const absChar = charOffset + (e.charIndex ?? 0)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let len: number | undefined = (e as any).charLength
      if (!len) {
        const slice = fullTextRef.current.slice(absChar)
        const m = slice.match(/^\S+/)
        len = m ? m[0].length : undefined
      }
      updateHighlight(absChar, len)
    }
    utt.onend = () => {
      speakStartRef.current = 0
      setPlaying(false)
      stopTicker()
      stopKeepAlive()
      setElapsed(duration)
      if (wordHighlightRef.current) wordHighlightRef.current.clear()
      lastWordStartRef.current = -1
    }
    utt.onerror = () => {
      speakStartRef.current = 0
      setPlaying(false)
      stopTicker()
      stopKeepAlive()
    }
    charOffsetRef.current = charOffset
    const doSpeak = () => { window.speechSynthesis?.speak(utt) }
    if (synth.speaking || synth.pending) {
      // Cancel the active utterance, then wait for the engine to flush before
      // queuing the new one (Chrome drops speak() called immediately after cancel).
      synth.cancel()
      setTimeout(doSpeak, 50)
    } else {
      // Nothing active — call synchronously to preserve the user-activation gesture.
      // A setTimeout here would break Chrome's activation check on the first play.
      doSpeak()
    }
  }

  const handlePlayPause = () => {
    if (!supported) return
    ensureMap()
    // Always recompute duration so charPerSecRef.current is current even if the
    // text map was empty when the component first mounted (timing race).
    recomputeDuration(rate)
    if (!fullTextRef.current.trim()) return
    if (!playing) {
      setExpanded(true)
      const startChar = elapsed > 0 ? Math.round(elapsed * charPerSecRef.current) : 0
      speakFromOffset(startChar, rate)
    } else {
      window.speechSynthesis.cancel()
      speakStartRef.current = 0
      setPlaying(false)
      stopTicker()
      stopKeepAlive()
      if (wordHighlightRef.current) wordHighlightRef.current.clear()
      lastWordStartRef.current = -1
    }
  }

  const skipBy = (deltaSec: number) => {
    if (!duration) return
    const target = Math.max(0, Math.min(duration, elapsed + deltaSec))
    setElapsed(target)
    if (playing) {
      const startChar = Math.round(target * charPerSecRef.current)
      speakFromOffset(startChar, rate)
    }
  }

  const cycleRate = () => {
    const idx = RATE_OPTIONS.indexOf(rate as typeof RATE_OPTIONS[number])
    const next = RATE_OPTIONS[(idx + 1) % RATE_OPTIONS.length]
    setRate(next)
    const newDuration = recomputeDuration(next)
    if (playing) {
      const ratio = duration ? elapsed / duration : 0
      const newElapsed = ratio * newDuration
      setElapsed(newElapsed)
      const startChar = Math.round(
        newElapsed * (fullTextRef.current.length / Math.max(newDuration, 1)),
      )
      speakFromOffset(startChar, next)
    }
  }

  if (!supported) return null

  const progress = duration > 0 ? Math.min(1, elapsed / duration) : 0

  const seekTo = (ratio: number) => {
    if (!duration) return
    const target = Math.max(0, Math.min(duration, ratio * duration))
    setElapsed(target)
    if (playing) {
      const startChar = Math.round(target * charPerSecRef.current)
      speakFromOffset(startChar, rate)
    }
  }

  if (!expanded) {
    return (
      <button
        type="button"
        className="blog-listen-pill"
        onClick={handlePlayPause}
        aria-label="Listen to article"
      >
        <span className="blog-listen-pill__icon" aria-hidden="true">
          <PlayIcon size={14} weight="fill" />
        </span>
        <span className="blog-listen-pill__label">Listen to article</span>
        <span className="blog-listen-pill__sep" aria-hidden="true" />
        <span className="blog-listen-pill__time">{formatTime(duration)}</span>
      </button>
    )
  }

  return (
    <div className="blog-listen-player blog-listen-player--expanded" role="region" aria-label="Article audio player">
      <button
        type="button"
        className="blog-listen-player__play"
        onClick={handlePlayPause}
        aria-label={playing ? 'Pause article' : 'Play article'}
      >
        {playing ? <PauseIcon size={18} weight="fill" /> : <PlayIcon size={18} weight="fill" />}
      </button>

      <span className="blog-listen-player__time">
        {formatTime(elapsed)}<span className="blog-listen-player__time-sep"> / </span>{formatTime(duration)}
      </span>

      <button
        type="button"
        className="blog-listen-player__skip"
        onClick={() => skipBy(-SKIP_SECONDS)}
        aria-label={`Skip back ${SKIP_SECONDS} seconds`}
      >
        <ArrowCounterClockwiseIcon size={14} weight="bold" />
        <span className="blog-listen-player__skip-num">{SKIP_SECONDS}</span>
      </button>

      <div
        className="blog-listen-player__scrubber"
        role="slider"
        tabIndex={0}
        aria-label="Audio position"
        aria-valuemin={0}
        aria-valuemax={duration || 1}
        aria-valuenow={Math.round(elapsed)}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          seekTo((e.clientX - rect.left) / rect.width)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); skipBy(-5) }
          if (e.key === 'ArrowRight') { e.preventDefault(); skipBy(5) }
        }}
      >
        <div className="blog-listen-player__scrubber-track" />
        <div className="blog-listen-player__scrubber-fill" style={{ width: `${progress * 100}%` }} />
        <div className="blog-listen-player__scrubber-thumb" style={{ left: `${progress * 100}%` }} />
      </div>

      <button
        type="button"
        className="blog-listen-player__skip"
        onClick={() => skipBy(SKIP_SECONDS)}
        aria-label={`Skip ahead ${SKIP_SECONDS} seconds`}
      >
        <span className="blog-listen-player__skip-num">{SKIP_SECONDS}</span>
        <ArrowClockwiseIcon size={14} weight="bold" />
      </button>

      <button
        type="button"
        className="blog-listen-player__rate"
        onClick={cycleRate}
        aria-label={`Playback speed ${rate} times. Click to change.`}
      >
        {rate}x
      </button>
    </div>
  )
}

// ─── Public entry point ────────────────────────────────────────────────────────

export function BlogListenButton({ targetSelector, text, audioSrc, audioTimings }: BlogListenButtonProps) {
  // Key on `audioSrc` so the player subtree fully remounts when the user
  // navigates between posts. Without this, the previous post's <audio> element,
  // word-timings, and elapsed/expanded state leak into the new post.
  if (audioSrc) {
    return (
      <AudioSrcPlayer
        key={audioSrc}
        audioSrc={audioSrc}
        audioTimings={audioTimings}
        targetSelector={targetSelector}
      />
    );
  }
  return <WebSpeechPlayer key="tts" targetSelector={targetSelector} text={text} />
}
